import { gql, useMutation, useQuery } from "@apollo/client";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import {
  Button,
  Input,
  SimpleDialog,
  Tag,
  Text,
  useToastContext,
} from "@ttc3k/trekker";
import { Flex, styled } from "styled-system/jsx";
import { useOrgHome } from "apps/home/hooks";
import { useAppContext } from "useAppContext";
import {
  EnumOrganizationMetadataDisabledApps,
  EnumOrganizationMetadataStorefrontMode,
} from "gql/generated";
import { useImpersonation } from "shared/contextProviders/impersonation";
import { InlineSectionTitle, SimpleLoader } from "shared/components";
import { formatRenewalDate } from "shared/utils/formatDate";
import { addMonths, format } from "date-fns";
import { OrgSettingsToggle } from "../../components";
import { formatTestDate } from "./formatTestDate";
import {
  MembershipFlowPanel,
  type TestEmailKind,
  type TestMembershipFlow,
  type TestScenario,
} from "./MembershipFlowPanel";

const settingsCardProps = {
  flexDir: "column" as const,
  gap: "300" as const,
  p: "300" as const,
  bgColor: "bg.wa.lightest" as const,
  border: "1px solid" as const,
  borderColor: "border.light" as const,
  borderRadius: "200" as const,
};

const TEST_RECORDS = gql`
  query TestRecordsByOrganization($organizationId: String!) {
    testRecordsByOrganization(organizationId: $organizationId) {
      membershipEnabled
      membershipFlow {
        broadcastId
        broadcastName
        checkoutMode
        renewalsEnabled
        policy {
          renewalType
          term
          termYears
          fixedRenewalMonth
          fixedRenewalDay
          reminderLeadDays
          graceDays
          neverExpires
          proRateEnabled
        }
        emails { kind custom }
        scenarios { state available reason availableFrom nextRenewalDate earlyWindowOpens }
        prorationAvailable
      }
      cap
      count
      showTestListings
      hasTestStorefront
      disabledApps
      businessTiers { _id name }
      individualTiers { _id name }
      records {
        _id
        entityKind
        operatorEntityId
        userId
        personId
        email
        name
        listingState
        membershipState
        tierTagId
        tierName
        statusTagNames
        renewalPhase
        renewalDate
        claimed
        inviteSent
        addedListingCount
        simulatedNow
        createdAt
      }
    }
  }
`;

const SET_SIMULATED_DATE = gql`
  mutation TestRecordSetSimulatedDate($id: MongoID!, $date: String) {
    testRecordSetSimulatedDate(id: $id, date: $date) { _id simulatedNow }
  }
`;

/** Paused for now; must match SIMULATED_CLOCK_ENABLED in backend simulatedClock.service.ts. */
const SIMULATED_DATE_ENABLED = false;

const SIMULATED_DATE_PRESETS = [
  { key: "PLUS_1M", months: 1 },
  { key: "PLUS_11M", months: 11 },
  { key: "PLUS_12M", months: 12 },
  { key: "PLUS_15M", months: 15 },
] as const;

const toYmd = (date: Date) => format(date, "yyyy-MM-dd");
const formatYmd = (ymd: string) =>
  formatRenewalDate(new Date(`${ymd}T12:00:00Z`), "MMM d, yyyy");

const CREATE = gql`
  mutation TestRecordCreate($input: TestRecordCreateInput!) {
    testRecordCreate(input: $input) { _id name }
  }
`;
const RESET = gql`
  mutation TestRecordReset($id: MongoID!, $listingState: TestRecordListingState, $membershipState: TestRecordMembershipState, $tierTagId: String) {
    testRecordReset(id: $id, listingState: $listingState, membershipState: $membershipState, tierTagId: $tierTagId) { _id }
  }
`;
const INVITE = gql`
  mutation TestRecordSendClaimInvite($id: MongoID!) {
    testRecordSendClaimInvite(id: $id) { _id inviteSent }
  }
`;
const DELETE_ONE = gql`
  mutation TestRecordDelete($id: MongoID!) { testRecordDelete(id: $id) }
`;
const DELETE_ALL = gql`
  mutation TestRecordDeleteAll($organizationId: String!) {
    testRecordDeleteAll(organizationId: $organizationId)
  }
`;
const RENEWAL = gql`
  mutation TestRecordRunRenewalCheck($organizationId: String!) {
    testRecordRunRenewalCheck(organizationId: $organizationId) { reminded expired }
  }
`;
const SEND_EMAIL = gql`
  mutation TestRecordSendRenewalEmail($id: MongoID!, $kind: TestRenewalEmailKind!) {
    testRecordSendRenewalEmail(id: $id, kind: $kind) { sent skipped }
  }
`;
const SHOW = gql`
  mutation TestRecordSetShowInWidgets($organizationId: String!, $show: Boolean!) {
    testRecordSetShowInWidgets(organizationId: $organizationId, show: $show)
  }
`;

const LISTING_STATES = [
  "UNCLAIMED_PROSPECT",
  "ONBOARDING_INCOMPLETE",
  "ONBOARDED",
  "AWAITING_APPROVAL",
] as const;
type MembershipState = "NONE" | TestScenario["state"];
type EntityKind = "LISTING" | "CONTACT" | "SIGNUP";

type RecordRow = {
  _id: string;
  entityKind: EntityKind;
  operatorEntityId?: string | null;
  userId: string;
  personId: string;
  email: string;
  name: string;
  listingState?: string | null;
  membershipState: string;
  tierName?: string | null;
  statusTagNames: string[];
  renewalPhase?: string | null;
  renewalDate?: string | null;
  claimed: boolean;
  inviteSent: boolean;
  addedListingCount: number;
  simulatedNow?: string | null;
};

export const TestTools = () => {
  const { t } = useTranslation("home", { keyPrefix: "VIEWS.SETTINGS.VIEWS.TEST_TOOLS" });
  const { organization } = useOrgHome();
  const organizationId = organization?._id ?? "";
  const { toastFactory } = useToastContext();
  const { startTestSession } = useImpersonation();
  const navigate = useNavigate();
  const location = useLocation();
  const [creating, setCreating] = useState(false);
  const [kind, setKind] = useState<EntityKind>("LISTING");
  const [listingState, setListingState] = useState<(typeof LISTING_STATES)[number]>("ONBOARDED");
  const [membershipState, setMembershipState] = useState<MembershipState>("NONE");
  const [tierTagId, setTierTagId] = useState("");
  const [email, setEmail] = useState("");
  const { dbUser } = useAppContext();
  const [confirmDeleteAll, setConfirmDeleteAll] = useState(false);
  const [pendingAction, setPendingAction] = useState<{
    kind: "RESET" | "SCENARIO" | "DELETE" | "INVITE" | "EMAIL";
    record: RecordRow;
    membershipState?: MembershipState;
    emailKind?: TestEmailKind;
  } | null>(null);
  const [picker, setPicker] = useState<{
    recordId: string;
    type: "EMAIL" | "SCENARIO";
  } | null>(null);
  const [actionRunning, setActionRunning] = useState(false);

  const { data, loading, refetch } = useQuery(TEST_RECORDS, {
    variables: { organizationId },
    skip: !organizationId,
    fetchPolicy: "network-only",
  });
  const payload = data?.testRecordsByOrganization;
  const flow: TestMembershipFlow | null = payload?.membershipFlow ?? null;
  const scenarios: TestScenario[] = flow?.scenarios ?? [];
  const [createRecord, { loading: creatingRecord }] = useMutation(CREATE);
  const [sendEmail] = useMutation(SEND_EMAIL);
  const [resetRecord] = useMutation(RESET);
  const [sendInvite] = useMutation(INVITE);
  const [deleteOne] = useMutation(DELETE_ONE);
  const [deleteAll, { loading: deletingAll }] = useMutation(DELETE_ALL);
  const [runRenewal, { loading: runningRenewal }] = useMutation(RENEWAL);
  const [setShow, { loading: savingShow }] = useMutation(SHOW);
  const [setSimulatedDate] = useMutation(SET_SIMULATED_DATE);
  const [dateDrafts, setDateDrafts] = useState<Record<string, string>>({});
  const [savingDateId, setSavingDateId] = useState<string | null>(null);

  const saveSimulatedDate = async (record: RecordRow, date: string | null) => {
    if (savingDateId) return;
    if ((record.simulatedNow ?? null) === date) return;
    setSavingDateId(record._id);
    await run(async () => {
      await setSimulatedDate({ variables: { id: record._id, date } });
      setDateDrafts(prev => {
        const next = { ...prev };
        delete next[record._id];
        return next;
      });
    }, t("SIMULATED_DATE_SAVED"));
    setSavingDateId(null);
  };

  const tiers = kind === "CONTACT" ? payload?.individualTiers ?? [] : payload?.businessTiers ?? [];
  const atCap = (payload?.count ?? 0) >= (payload?.cap ?? 10);
  const disabledApps = organization?.metadata?.disabledApps ?? [];
  const appOn = (app: EnumOrganizationMetadataDisabledApps) =>
    !disabledApps.includes(app);

  const storefrontMode =
    organization?.metadata?.storefrontMode ??
    EnumOrganizationMetadataStorefrontMode.Cad;
  const connectedId = (value: unknown) => (value ? String(value) : "");
  const liveStorefrontIds =
    storefrontMode === EnumOrganizationMetadataStorefrontMode.Usd
      ? [connectedId(organization?.metadata?.connectedUsdStorefront)]
      : storefrontMode === EnumOrganizationMetadataStorefrontMode.Both
        ? [
            connectedId(organization?.metadata?.connectedStorefront),
            connectedId(organization?.metadata?.connectedUsdStorefront),
          ]
        : [connectedId(organization?.metadata?.connectedStorefront)];
  const testStorefrontIds =
    storefrontMode === EnumOrganizationMetadataStorefrontMode.Usd
      ? [connectedId(organization?.metadata?.connectedUsdTestStorefront)]
      : storefrontMode === EnumOrganizationMetadataStorefrontMode.Both
        ? [
            connectedId(organization?.metadata?.connectedTestStorefront),
            connectedId(organization?.metadata?.connectedUsdTestStorefront),
          ]
        : [connectedId(organization?.metadata?.connectedTestStorefront)];
  const hasLiveStorefront = liveStorefrontIds.some(Boolean);
  const hasTestStorefront = testStorefrontIds.some(Boolean);
  const showLiveStorefrontWarning =
    organization?.metadata?.storefrontOrgConnectionsEnabled !== false &&
    appOn(EnumOrganizationMetadataDisabledApps.Storefront) &&
    hasLiveStorefront &&
    !hasTestStorefront;

  const capabilities = useMemo(() => {
    const items: string[] = [];
    if (appOn(EnumOrganizationMetadataDisabledApps.Directory)) {
      items.push("CLAIM", "APPROVAL", "BROADCASTS");
    }
    if (appOn(EnumOrganizationMetadataDisabledApps.People)) items.push("PEOPLE");
    if (appOn(EnumOrganizationMetadataDisabledApps.Notes)) items.push("NOTES");
    if (appOn(EnumOrganizationMetadataDisabledApps.Events)) items.push("EVENTS");
    if (payload?.membershipEnabled) items.push("MEMBERSHIP");
    return items;
  }, [disabledApps, payload?.membershipEnabled]);

  const nextTestEmail = () => {
    const own = (dbUser?.email ?? "").toLowerCase();
    const at = own.indexOf("@");
    if (at < 1) return "";
    const used = new Set(
      (payload?.records ?? []).map((record: RecordRow) =>
        record.email.toLowerCase(),
      ),
    );
    for (let n = 1; n <= 100; n++) {
      const candidate = `${own.slice(0, at)}+test${n}${own.slice(at)}`;
      if (!used.has(candidate)) return candidate;
    }
    return "";
  };

  const openCreate = () => {
    if (creating) {
      setCreating(false);
      return;
    }
    setEmail(nextTestEmail());
    setCreating(true);
  };

  const confirmPendingAction = async () => {
    if (!pendingAction || actionRunning) return;
    const { kind: actionKind, record } = pendingAction;
    setActionRunning(true);
    let success = "";
    await run(async () => {
      if (actionKind === "RESET" || actionKind === "SCENARIO") {
        await resetRecord({
          variables: {
            id: record._id,
            listingState: record.listingState,
            membershipState: pendingAction.membershipState ?? record.membershipState,
          },
        });
        success = t("RESET_DONE");
      } else if (actionKind === "DELETE") {
        await deleteOne({ variables: { id: record._id } });
        success = t("DELETED");
      } else if (actionKind === "EMAIL") {
        const result = await sendEmail({
          variables: { id: record._id, kind: pendingAction.emailKind },
        });
        success =
          (result.data?.testRecordSendRenewalEmail?.sent ?? 0) > 0
            ? t("EMAIL_SENT", { email: record.email })
            : t("EMAIL_SKIPPED");
      } else {
        await sendInvite({ variables: { id: record._id } });
        success = t("INVITE_SENT");
      }
      if (success) toastFactory.success({ title: success });
    }, "");
    setActionRunning(false);
    setPendingAction(null);
    setPicker(null);
  };

  const scenarioHint = (scenario?: TestScenario) => {
    if (!scenario) return "";
    if (scenario.available && scenario.earlyWindowOpens) {
      return t("SCENARIO_HINT.RENEWING_EARLY", {
        date: formatTestDate(scenario.nextRenewalDate),
        opens: formatTestDate(scenario.earlyWindowOpens),
      });
    }
    if (scenario.available) {
      return scenario.nextRenewalDate
        ? t(`SCENARIO_HINT.${scenario.state}`, {
            date: formatTestDate(scenario.nextRenewalDate),
          })
        : t("SCENARIO_HINT.NO_DATES");
    }
    return t(`SCENARIO_UNAVAILABLE.${scenario.reason ?? "NOT_REACHABLE"}`, {
      date: formatTestDate(scenario.availableFrom),
    });
  };

  const hasListing = (record: RecordRow) =>
    !!record.operatorEntityId || record.addedListingCount > 0;
  const canEmail = (record: RecordRow) =>
    !!flow?.renewalsEnabled && (flow?.emails.length ?? 0) > 0 && hasListing(record);
  const canChangeScenario = (record: RecordRow) =>
    !!flow && record.entityKind !== "SIGNUP";
  const membershipSettingsHref = `/app/organization/${organizationId}/home?view=SETTINGS&settingsSubView=MEMBERSHIP_TIERS`;

  const run = async (work: () => Promise<unknown>, success: string) => {
    try {
      await work();
      if (success) toastFactory.success({ title: success });
      await refetch();
    } catch (error) {
      toastFactory.error({
        title: t("ERROR"),
        description: error instanceof Error ? error.message : undefined,
      });
    }
  };

  if (!organizationId || loading) return <SimpleLoader />;

  return (
    <Flex flexDir="column" gap="250" w="full">
      <InlineSectionTitle title={t("TITLE")} />
      <Text color="text.mid">{t("DESCRIPTION")}</Text>
      {capabilities.length > 0 && (
        <Flex flexDir="column" gap="100">
          <Text color="text.mid">{t("RECOMMEND")}</Text>
          <styled.ul
            display="flex"
            flexDir="column"
            gap="75"
            pl="400"
            listStyleType="disc"
          >
            {capabilities.map(item => (
              <Text as="li" key={item} color="text.mid">
                {t(`CAN_TEST.${item}`)}
              </Text>
            ))}
          </styled.ul>
        </Flex>
      )}

      {flow && (
        <MembershipFlowPanel
          flow={flow}
          organizationId={organizationId}
          membershipSettingsHref={membershipSettingsHref}
        />
      )}

      <Flex gap="100" flexWrap="wrap" mt="300">
        <Button visual="accent.blue" mode="dark" width="auto" disabled={atCap} onClick={openCreate}>
          {t("CREATE")}
        </Button>
        {flow?.renewalsEnabled && (
          <Button
            visual="accent.blue"
            mode="light"
            width="auto"
            disabled={runningRenewal || (payload?.count ?? 0) === 0}
            onClick={() =>
              void run(async () => {
                const result = await runRenewal({ variables: { organizationId } });
                const check = result.data?.testRecordRunRenewalCheck;
                toastFactory.success({
                  title: t("RENEWAL_DONE", {
                    reminded: check?.reminded ?? 0,
                    expired: check?.expired ?? 0,
                  }),
                });
              }, "")
            }
          >
            {t("RUN_RENEWAL")}
          </Button>
        )}
        <Button visual="destructive" mode="light" width="auto" disabled={(payload?.count ?? 0) === 0} onClick={() => setConfirmDeleteAll(true)}>
          {t("DELETE_ALL")}
        </Button>
      </Flex>

      <SimpleDialog
        open={!!pendingAction}
        title={
          pendingAction
            ? t(`ACTION_MODAL.${pendingAction.kind}.TITLE`, {
                name: pendingAction.record.name,
                emailKind: pendingAction.emailKind
                  ? t(`FLOW.EMAIL.${pendingAction.emailKind}`)
                  : "",
                scenario: pendingAction.membershipState
                  ? t(`MEMBERSHIP.${pendingAction.membershipState}`)
                  : "",
              })
            : ""
        }
        message={
          pendingAction
            ? t(`ACTION_MODAL.${pendingAction.kind}.MESSAGE`, {
                name: pendingAction.record.name,
                email: pendingAction.record.email,
              })
            : ""
        }
        cancelButtonCopy={t("CANCEL")}
        onCancelClick={() => {
          if (!actionRunning) setPendingAction(null);
        }}
        confirmButtonCopy={
          pendingAction
            ? t(`ACTION_MODAL.${pendingAction.kind}.CONFIRM`)
            : ""
        }
        confirmButtonVisual={
          pendingAction?.kind === "DELETE" ? "destructive" : undefined
        }
        onConfirmClick={() => void confirmPendingAction()}
        size="md"
        portal
      />

      <SimpleDialog
        open={confirmDeleteAll}
        title={t("DELETE_ALL_MODAL.TITLE")}
        message={t("DELETE_ALL_MODAL.MESSAGE", { count: payload?.count ?? 0 })}
        cancelButtonCopy={t("CANCEL")}
        onCancelClick={() => setConfirmDeleteAll(false)}
        confirmButtonCopy={t("DELETE_ALL_CONFIRM")}
        confirmButtonVisual="destructive"
        onConfirmClick={() => {
          if (deletingAll) return;
          void run(async () => {
            await deleteAll({ variables: { organizationId } });
            setConfirmDeleteAll(false);
          }, t("DELETED"));
        }}
        size="md"
        portal
      />

      {creating && (
        <Flex {...settingsCardProps}>
          <Text visual="bodySemiBold" mb="150">{t("CREATE")}</Text>
          <Flex gap="100" mb="150" flexWrap="wrap">
            {(["LISTING", "CONTACT", "SIGNUP"] as const).map(value => (
              <Tag
                key={value}
                cursor="pointer"
                mode={kind === value ? "dark" : "light"}
                visual={kind === value ? "primary" : undefined}
                onClick={() => {
                  setKind(value);
                  setTierTagId("");
                  if (value === "SIGNUP") setMembershipState("NONE");
                }}
              >
                {t(`KIND.${value}`)}
              </Tag>
            ))}
          </Flex>
          {kind === "SIGNUP" && (
            <Text visual="smallRegular" color="text.mid" mb="150">
              {flow?.broadcastId ? t("SIGNUP_HELP_BROADCAST") : t("SIGNUP_HELP")}
            </Text>
          )}
          {kind === "LISTING" && (
            <Flex gap="100" mb="150" flexWrap="wrap">
              {LISTING_STATES.map(value => (
                <Tag key={value} cursor="pointer" mode={listingState === value ? "dark" : "light"} visual={listingState === value ? "primary" : undefined} onClick={() => setListingState(value)}>
                  {t(`LISTING.${value}`)}
                </Tag>
              ))}
            </Flex>
          )}
          {flow && kind !== "SIGNUP" && (
            <>
              <Flex gap="100" mb="100" flexWrap="wrap">
                {(["NONE", ...scenarios.map(item => item.state)] as MembershipState[]).map(value => {
                  const scenario = scenarios.find(item => item.state === value);
                  const disabled = !!scenario && !scenario.available;
                  const selected = membershipState === value;
                  return (
                    <Tag
                      key={value}
                      cursor={disabled ? "not-allowed" : "pointer"}
                      opacity={disabled ? 0.5 : 1}
                      mode={selected ? "dark" : "light"}
                      visual={selected ? "primary" : undefined}
                      title={disabled ? scenarioHint(scenario) : undefined}
                      onClick={() => {
                        if (!disabled) setMembershipState(value);
                      }}
                    >
                      {t(`MEMBERSHIP.${value}`)}
                    </Tag>
                  );
                })}
              </Flex>
              <Text visual="smallRegular" color="text.mid" mb="150">
                {membershipState === "NONE"
                  ? t("SCENARIO_HINT.NONE")
                  : scenarioHint(scenarios.find(item => item.state === membershipState))}
              </Text>
              {membershipState !== "NONE" && (
                <Flex gap="100" mb="150" flexWrap="wrap">
                  {(tiers ?? []).map((tier: { _id: string; name: string }) => (
                    <Tag key={tier._id} cursor="pointer" mode={tierTagId === tier._id ? "dark" : "light"} visual={tierTagId === tier._id ? "primary" : undefined} onClick={() => setTierTagId(tier._id)}>
                      {tier.name}
                    </Tag>
                  ))}
                </Flex>
              )}
            </>
          )}
          <Input
            value={email}
            placeholder={t("EMAIL_PLACEHOLDER")}
            onChange={e => setEmail(e.target.value)}
            bg="white"
            mb="150"
          />
          <Text visual="smallRegular" color="text.mid" mb="150">{t("EMAIL_HELP")}</Text>
          <Flex gap="100">
          <Button
            visual="accent.blue"
            mode="dark"
            width="auto"
            isLoading={creatingRecord}
            disabled={creatingRecord || (membershipState !== "NONE" && !tierTagId)}
            onClick={() =>
              void run(async () => {
                await createRecord({
                  variables: {
                    input: {
                      organizationId,
                      entityKind: kind,
                      listingState: kind === "LISTING" ? listingState : undefined,
                      membershipState: flow && kind !== "SIGNUP" ? membershipState : "NONE",
                      tierTagId: membershipState !== "NONE" ? tierTagId : undefined,
                      email: email.trim() || undefined,
                    },
                  },
                });
                setCreating(false);
                setEmail("");
              }, t("CREATED"))
            }
          >
            {t("CREATE_CONFIRM")}
          </Button>
          <Button
            visual="accent.blue"
            mode="light"
            width="auto"
            onClick={() => {
              setCreating(false);
              setEmail("");
            }}
          >
            {t("CANCEL")}
          </Button>
          </Flex>
        </Flex>
      )}

      {(payload?.records ?? []).map((record: RecordRow) => (
        <Flex key={record._id} {...settingsCardProps}>
          <Text visual="bodySemiBold">{record.name}</Text>
          <Text visual="smallRegular" color="text.mid">
            {[
              t(`KIND.${record.entityKind}`),
              record.email,
              record.listingState ? t(`LISTING.${record.listingState}`) : null,
              record.entityKind === "LISTING"
                ? record.claimed ? t("CLAIMED") : t("UNCLAIMED")
                : null,
              record.inviteSent ? t("INVITED") : null,
              record.tierName,
              record.membershipState !== "NONE" ? t(`MEMBERSHIP.${record.membershipState}`) : null,
              record.renewalDate
                ? t("RENEWAL_DATE", { date: formatTestDate(record.renewalDate) })
                : null,
              record.renewalPhase
                ? t(`PHASE.${record.renewalPhase}`, { defaultValue: record.renewalPhase })
                : null,
              record.simulatedNow
                ? t("AS_OF", { date: formatYmd(record.simulatedNow) })
                : null,
              record.addedListingCount > 0
                ? t("ADDED_LISTINGS", { count: record.addedListingCount })
                : null,
            ].filter(Boolean).join(" · ")}
          </Text>
          {SIMULATED_DATE_ENABLED && payload?.membershipEnabled && (
            <Flex flexDir="column" gap="75" mt="150">
              <Text visual="smallSemiBold">{t("VIEW_AS_OF")}</Text>
              <Flex gap="100" flexWrap="wrap" alignItems="center">
                <Input
                  type="date"
                  w="auto"
                  bg="white"
                  disabled={savingDateId === record._id}
                  value={dateDrafts[record._id] ?? record.simulatedNow ?? ""}
                  onChange={e =>
                    setDateDrafts(prev => ({
                      ...prev,
                      [record._id]: e.target.value,
                    }))
                  }
                  onBlur={() => {
                    const draft = dateDrafts[record._id];
                    if (draft === undefined) return;
                    void saveSimulatedDate(record, draft || null);
                  }}
                  onKeyDown={e => {
                    if (e.key === "Enter") e.currentTarget.blur();
                  }}
                />
                <Tag
                  cursor="pointer"
                  mode={!record.simulatedNow ? "dark" : "light"}
                  visual={!record.simulatedNow ? "primary" : undefined}
                  onClick={() => void saveSimulatedDate(record, null)}
                >
                  {t("PRESET.TODAY")}
                </Tag>
                {SIMULATED_DATE_PRESETS.map(preset => {
                  const value = toYmd(addMonths(new Date(), preset.months));
                  const selected = record.simulatedNow === value;
                  return (
                    <Tag
                      key={preset.key}
                      cursor="pointer"
                      mode={selected ? "dark" : "light"}
                      visual={selected ? "primary" : undefined}
                      onClick={() => void saveSimulatedDate(record, value)}
                    >
                      {t(`PRESET.${preset.key}`)}
                    </Tag>
                  );
                })}
              </Flex>
              <Text visual="smallRegular" color="text.mid">
                {t("VIEW_AS_OF_HELP")}
              </Text>
            </Flex>
          )}
          <Flex gap="100" mt="150" flexWrap="wrap">
            <Button
              size="sm"
              visual="accent.blue"
              mode="dark"
              width="auto"
              onClick={() => {
                startTestSession(
                  record.userId,
                  record.name,
                  `${location.pathname}${location.search}`,
                  record.simulatedNow,
                );
                window.location.assign("/app");
              }}
            >
              {t("ACT_AS")}
            </Button>
            {record.entityKind === "LISTING" && (
              <Button
                size="sm"
                visual="accent.blue"
                mode="light"
                width="auto"
                disabled={record.inviteSent}
                onClick={() => setPendingAction({ kind: "INVITE", record })}
              >
                {t("SEND_INVITE")}
              </Button>
            )}
            {record.operatorEntityId && (
              <Button size="sm" visual="accent.blue"
                mode="light" width="auto" onClick={() => navigate(`/app/organization/${organizationId}/directory/${record.operatorEntityId}`)}>
                {t("OPEN_DIRECTORY")}
              </Button>
            )}
            <Button size="sm" visual="accent.blue"
                mode="light" width="auto" onClick={() => navigate(`/app/organization/${organizationId}/people`)}>
              {t("OPEN_PEOPLE")}
            </Button>
            {canEmail(record) && (
              <Button
                size="sm"
                visual="accent.blue"
                mode="light"
                width="auto"
                onClick={() =>
                  setPicker(current =>
                    current?.recordId === record._id && current.type === "EMAIL"
                      ? null
                      : { recordId: record._id, type: "EMAIL" },
                  )
                }
              >
                {t("SEND_EMAIL")}
              </Button>
            )}
            {canChangeScenario(record) && (
              <Button
                size="sm"
                visual="accent.blue"
                mode="light"
                width="auto"
                onClick={() =>
                  setPicker(current =>
                    current?.recordId === record._id && current.type === "SCENARIO"
                      ? null
                      : { recordId: record._id, type: "SCENARIO" },
                  )
                }
              >
                {t("CHANGE_SCENARIO")}
              </Button>
            )}
            <Button
              size="sm"
              visual="accent.blue"
              mode="light"
              width="auto"
              onClick={() => setPendingAction({ kind: "RESET", record })}
            >
              {t("RESET")}
            </Button>
            <Button
              size="sm"
              visual="destructive"
              mode="light"
              width="auto"
              onClick={() => setPendingAction({ kind: "DELETE", record })}
            >
              {t("DELETE")}
            </Button>
          </Flex>
          {picker?.recordId === record._id && picker.type === "EMAIL" && (
            <Flex flexDir="column" gap="75" mt="100">
              <Text visual="smallRegular" color="text.mid">{t("SEND_EMAIL_HELP")}</Text>
              <Flex gap="100" flexWrap="wrap">
                {(flow?.emails ?? []).map(email => (
                  <Tag
                    key={email.kind}
                    cursor="pointer"
                    onClick={() =>
                      setPendingAction({ kind: "EMAIL", record, emailKind: email.kind })
                    }
                  >
                    {t(`FLOW.EMAIL.${email.kind}`)} ·{" "}
                    {email.custom ? t("FLOW.EMAIL_CUSTOM") : t("FLOW.EMAIL_DEFAULT")}
                  </Tag>
                ))}
              </Flex>
            </Flex>
          )}
          {picker?.recordId === record._id && picker.type === "SCENARIO" && (
            <Flex flexDir="column" gap="75" mt="100">
              <Flex gap="100" flexWrap="wrap">
                {(["NONE", ...scenarios.map(item => item.state)] as MembershipState[]).map(value => {
                  const scenario = scenarios.find(item => item.state === value);
                  const disabled =
                    (!!scenario && !scenario.available) ||
                    (value !== "NONE" && !record.tierName);
                  return (
                    <Tag
                      key={value}
                      cursor={disabled ? "not-allowed" : "pointer"}
                      opacity={disabled ? 0.5 : 1}
                      mode={record.membershipState === value ? "dark" : "light"}
                      visual={record.membershipState === value ? "primary" : undefined}
                      title={scenario ? scenarioHint(scenario) : undefined}
                      onClick={() => {
                        if (!disabled) {
                          setPendingAction({ kind: "SCENARIO", record, membershipState: value });
                        }
                      }}
                    >
                      {t(`MEMBERSHIP.${value}`)}
                    </Tag>
                  );
                })}
              </Flex>
              {!record.tierName && (
                <Text visual="smallRegular" color="text.mid">{t("CHANGE_SCENARIO_NO_TIER")}</Text>
              )}
            </Flex>
          )}
        </Flex>
      ))}
      {(payload?.records ?? []).length === 0 && (
        <Text visual="smallRegular" color="text.mid">{t("EMPTY")}</Text>
      )}

      <InlineSectionTitle title={t("SETTINGS")} containerProps={{ mt: "400" }} />
      <Flex {...settingsCardProps}>
        <OrgSettingsToggle
          title={t("SHOW_IN_WIDGETS")}
          description={t("SHOW_IN_WIDGETS_HELP")}
          checked={!!payload?.showTestListings}
          isSaving={savingShow}
          onCheckedChange={checked =>
            void run(
              () => setShow({ variables: { organizationId, show: checked } }),
              t("SHOW_SAVED"),
            )
          }
        />
        {showLiveStorefrontWarning && (
          <Text visual="smallRegular" color="text.mid">
            {t("NO_TEST_STOREFRONT")}
          </Text>
        )}
      </Flex>
    </Flex>
  );
};
